export class AudioRecorderService {
  constructor() {
    this.mediaRecorder = null;
    this.audioChunks = [];
    this.isRecording = false;
    this.isPaused = false;
    this.stream = null;
    this.startTime = null;
    this.pausedDuration = 0;
    this.pauseStartTime = null;
    this.callbacks = {};

    // Audio context for visualization
    this.audioContext = null;
    this.analyser = null;
    this.dataArray = null;
    this.animationFrame = null;

    // Supported MIME types in order of preference (prioritize size-efficient formats)
    this.mimeTypes = [
      'audio/webm;codecs=opus',  // Best compression with Opus codec
      'audio/ogg;codecs=opus',    // Alternative Opus container
      'audio/webm',               // Fallback WebM
      'audio/mp4;codecs=mp4a.40.2', // AAC in MP4
      'audio/ogg',                // Fallback Ogg
      'audio/mpeg'                // MP3 fallback
    ];

    // Find supported MIME type
    this.mimeType = this.mimeTypes.find(type =>
      MediaRecorder.isTypeSupported(type)
    ) || 'audio/webm';

    console.log('Selected audio format:', this.mimeType);
  }

  async initialize() {
    try {
      // Request microphone permission with optimized settings for voice
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000,  // Lower sample rate for voice (reduces size by ~64%)
          channelCount: 1      // Mono audio (reduces size by 50%)
        }
      });

      // Create media recorder with optimized bitrate for voice
      // Voice typically needs 16-32 kbps with Opus codec
      const bitrate = this.mimeType.includes('opus') ? 24000 : 64000;

      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType: this.mimeType,
        audioBitsPerSecond: bitrate  // Much lower bitrate for voice
      });

      console.log('Audio recorder initialized with bitrate:', bitrate);

      // Setup audio context for visualization
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const source = this.audioContext.createMediaStreamSource(this.stream);
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;
      source.connect(this.analyser);

      const bufferLength = this.analyser.frequencyBinCount;
      this.dataArray = new Uint8Array(bufferLength);

      // Setup event handlers
      this.setupEventHandlers();

      return true;
    } catch (error) {
      console.error('Failed to initialize audio recorder:', error);
      this.callbacks.onError?.('Microphone access denied or not available');
      return false;
    }
  }

  setupEventHandlers() {
    if (!this.mediaRecorder) return;

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        this.audioChunks.push(event.data);
      }
    };

    this.mediaRecorder.onstart = () => {
      console.log('Recording started');
      this.callbacks.onStart?.();
      this.startVisualization();
    };

    this.mediaRecorder.onstop = async () => {
      console.log('Recording stopped');
      this.stopVisualization();

      // Create blob from chunks
      let audioBlob = new Blob(this.audioChunks, { type: this.mimeType });

      // Log original size
      console.log('Original audio size:', (audioBlob.size / 1024).toFixed(1), 'KB');

      // Compress audio if it's too large (audioContext is still available here)
      if (audioBlob.size > 500 * 1024 && this.audioContext && this.audioContext.state !== 'closed') {
        try {
          audioBlob = await this.compressAudio(audioBlob);
          console.log('Compressed audio size:', (audioBlob.size / 1024).toFixed(1), 'KB');
        } catch (error) {
          console.log('Could not compress audio further:', error);
        }
      }

      // Calculate duration
      const duration = this.getDuration();

      // Create audio URL for playback
      const audioUrl = URL.createObjectURL(audioBlob);

      // Callback with recording data
      this.callbacks.onStop?.({
        blob: audioBlob,
        url: audioUrl,
        duration,
        mimeType: this.mimeType,
        timestamp: new Date().toISOString(),
        sizeKB: (audioBlob.size / 1024).toFixed(1)
      });

      // Cleanup AFTER blob assembly, compression, and callback
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
        this.stream = null;
      }
      if (this.audioContext && this.audioContext.state !== 'closed') {
        this.audioContext.close();
        this.audioContext = null;
      }
      this.mediaRecorder = null;

      // Reset for next recording
      this.audioChunks = [];
      this.startTime = null;
      this.pausedDuration = 0;
    };

    this.mediaRecorder.onpause = () => {
      console.log('Recording paused');
      this.pauseStartTime = Date.now();
      this.callbacks.onPause?.();
      this.stopVisualization();
    };

    this.mediaRecorder.onresume = () => {
      console.log('Recording resumed');
      if (this.pauseStartTime) {
        this.pausedDuration += Date.now() - this.pauseStartTime;
        this.pauseStartTime = null;
      }
      this.callbacks.onResume?.();
      this.startVisualization();
    };

    this.mediaRecorder.onerror = (event) => {
      console.error('MediaRecorder error:', event.error);
      this.callbacks.onError?.(event.error.message);
    };
  }

  async start(callbacks = {}) {
    this.callbacks = callbacks;

    if (!this.mediaRecorder) {
      const initialized = await this.initialize();
      if (!initialized) return false;
    }

    if (this.mediaRecorder.state === 'recording') {
      console.warn('Already recording');
      return false;
    }

    try {
      this.audioChunks = [];
      this.startTime = Date.now();
      this.pausedDuration = 0;
      this.mediaRecorder.start(1000); // Collect data every second
      this.isRecording = true;
      this.isPaused = false;

      return true;
    } catch (error) {
      console.error('Failed to start recording:', error);
      this.callbacks.onError?.(error.message);
      return false;
    }
  }

  pause() {
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.pause();
      this.isPaused = true;

      return true;
    }
    return false;
  }

  resume() {
    if (this.mediaRecorder && this.mediaRecorder.state === 'paused') {
      this.mediaRecorder.resume();
      this.isPaused = false;

      return true;
    }
    return false;
  }

  stop() {
    if (this.mediaRecorder &&
        (this.mediaRecorder.state === 'recording' || this.mediaRecorder.state === 'paused')) {
      this.mediaRecorder.stop();
      this.isRecording = false;
      this.isPaused = false;

      // NOTE: Stream, audioContext, and mediaRecorder cleanup is deferred to
      // the onstop handler so that compression can use audioContext if needed.

      return true;
    }
    return false;
  }

  getDuration() {
    if (!this.startTime) return 0;

    const totalTime = Date.now() - this.startTime;
    const actualRecordingTime = totalTime - this.pausedDuration;

    // If currently paused, subtract the current pause duration
    if (this.pauseStartTime) {
      const currentPauseDuration = Date.now() - this.pauseStartTime;
      return Math.max(0, actualRecordingTime - currentPauseDuration);
    }

    return Math.max(0, actualRecordingTime);
  }

  getFormattedDuration() {
    const duration = this.getDuration();
    const seconds = Math.floor(duration / 1000) % 60;
    const minutes = Math.floor(duration / 60000) % 60;
    const hours = Math.floor(duration / 3600000);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  // Audio visualization
  startVisualization() {
    if (!this.analyser || !this.dataArray) return;

    const visualize = () => {
      if (!this.isRecording || this.isPaused) return;

      this.animationFrame = requestAnimationFrame(visualize);
      this.analyser.getByteFrequencyData(this.dataArray);

      // Calculate average volume
      const average = this.dataArray.reduce((a, b) => a + b) / this.dataArray.length;
      const normalizedVolume = average / 255;

      // Callback with visualization data
      this.callbacks.onVisualizationData?.({
        dataArray: this.dataArray,
        volume: normalizedVolume
      });
    };

    visualize();
  }

  stopVisualization() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  // Compress audio using Web Audio API
  async compressAudio(audioBlob) {
    // Use existing audioContext if available, otherwise create a temporary one
    let ctx = this.audioContext;
    let tempCtx = false;
    if (!ctx || ctx.state === 'closed') {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      tempCtx = true;
    }

    return new Promise((resolve, _reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          // Decode the audio
          const audioBuffer = await ctx.decodeAudioData(e.target.result);

          // Create an offline context with lower sample rate
          const offlineContext = new OfflineAudioContext(
            1, // Mono
            audioBuffer.length * (16000 / audioBuffer.sampleRate), // Adjust length for new sample rate
            16000 // 16kHz sample rate for voice
          );

          // Create buffer source
          const source = offlineContext.createBufferSource();
          source.buffer = audioBuffer;
          source.connect(offlineContext.destination);
          source.start();

          // Render the downsampled audio
          const renderedBuffer = await offlineContext.startRendering();

          // Convert to WAV format (more compatible and often smaller for short recordings)
          const wavBlob = this.audioBufferToWav(renderedBuffer);

          // Clean up temporary context
          if (tempCtx) {
            ctx.close();
          }

          // Only use compressed version if it's actually smaller
          if (wavBlob.size < audioBlob.size) {
            return resolve(wavBlob);
          } else {
            return resolve(audioBlob);
          }
        } catch (error) {
          console.error('Compression failed:', error);
          if (tempCtx) {
            try { ctx.close(); } catch (_) { /* intentionally empty */ }
          }
          resolve(audioBlob); // Return original on error
        }
      };
      reader.readAsArrayBuffer(audioBlob);
    });
  }

  // Convert AudioBuffer to WAV format
  audioBufferToWav(buffer) {
    const length = buffer.length * buffer.numberOfChannels * 2;
    const arrayBuffer = new ArrayBuffer(44 + length);
    const view = new DataView(arrayBuffer);
    let offset = 0;
    let pos = 0;

    // Write WAV header
    const setUint16 = (data) => {
      view.setUint16(pos, data, true);
      pos += 2;
    };
    const setUint32 = (data) => {
      view.setUint32(pos, data, true);
      pos += 4;
    };

    // RIFF identifier
    setUint32(0x46464952); // "RIFF"
    setUint32(36 + length); // file length
    setUint32(0x45564157); // "WAVE"

    // fmt sub-chunk
    setUint32(0x20746d66); // "fmt "
    setUint32(16); // size
    setUint16(1); // PCM
    setUint16(buffer.numberOfChannels);
    setUint32(buffer.sampleRate);
    setUint32(buffer.sampleRate * 2 * buffer.numberOfChannels); // byte rate
    setUint16(buffer.numberOfChannels * 2); // block align
    setUint16(16); // bits per sample

    // data sub-chunk
    setUint32(0x61746164); // "data"
    setUint32(length);

    // Write interleaved data
    const channelData = [];
    for (let i = 0; i < buffer.numberOfChannels; i++) {
      channelData.push(buffer.getChannelData(i));
    }

    while (pos < arrayBuffer.byteLength) {
      for (let i = 0; i < buffer.numberOfChannels; i++) {
        let sample = channelData[i][offset];
        sample = Math.max(-1, Math.min(1, sample)); // Clamp
        sample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF; // Convert to 16-bit
        view.setInt16(pos, sample, true);
        pos += 2;
      }
      offset++;
    }

    return new Blob([arrayBuffer], { type: 'audio/wav' });
  }

  // Get recording state
  getState() {
    return {
      isRecording: this.isRecording,
      isPaused: this.isPaused,
      duration: this.getDuration(),
      formattedDuration: this.getFormattedDuration(),
      isInitialized: !!this.mediaRecorder
    };
  }

  // Clean up resources
  destroy() {
    this.stop();
    this.callbacks = {};
  }
}
